import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';

const SITE_URL = 'https://tomartmask.cz';

interface ReviewData {
  name: string;
  quote: string;
}

const StructuredData = () => {
  const { lang, t } = useLanguage();
  const [reviewCount, setReviewCount] = useState<number>(12);
  const [firstReview, setFirstReview] = useState<ReviewData | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { count } = await supabase
          .from('testimonials')
          .select('*', { count: 'exact', head: true })
          .eq('visible', true);
        if (!cancelled && typeof count === 'number' && count > 0) {
          setReviewCount(count);
        }

        const { data } = await supabase
          .from('testimonials')
          .select('name, quote')
          .eq('visible', true)
          .order('sort_order', { ascending: true })
          .limit(1);
        if (!cancelled && data && data.length > 0) {
          setFirstReview({ name: data[0].name, quote: data[0].quote });
        }
      } catch {
        // keep fallbacks
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const localBusiness = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${SITE_URL}/#business`,
    name: 'Tom Art Mask',
    image: `${SITE_URL}/og-image.jpg`,
    logo: `${SITE_URL}/logo.svg`,
    url: SITE_URL,
    telephone: '+420775291737',
    email: 'info@tomartmask.cz',
    description:
      lang === 'cs'
        ? 'Profesionální airbrush design hokejových masek na zakázku. 19+ let zkušeností.'
        : 'Professional custom airbrush hockey mask design. 19+ years of experience.',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Třebíč',
      addressCountry: 'CZ',
    },
    areaServed: [
      { '@type': 'Country', name: 'Czech Republic' },
      { '@type': 'Country', name: 'Slovakia' },
      { '@type': 'Country', name: 'Germany' },
      { '@type': 'Country', name: 'Austria' },
      { '@type': 'Place', name: 'Europe' },
    ],
    priceRange: '$$',
    sameAs: [
      'https://www.facebook.com/tomartmask',
      'https://www.instagram.com/tomartmask',
    ],
  };

  // Fallback review used during prerender / before DB loads
  const fallbackReview: ReviewData = {
    name: 'Spokojený brankář',
    quote:
      lang === 'cs'
        ? 'Maska předčila moje očekávání. Precizní práce, originální design a parádní komunikace.'
        : 'The mask exceeded my expectations. Precise work, original design and great communication.',
  };
  const reviewToUse = firstReview ?? fallbackReview;
  const reviewBody =
    reviewToUse.quote.length > 250
      ? reviewToUse.quote.slice(0, 247) + '...'
      : reviewToUse.quote;

  const product = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: lang === 'cs' ? 'Custom Airbrush Hokejová Maska' : 'Custom Airbrush Hockey Goalie Mask',
    image: `${SITE_URL}/og-image.jpg`,
    description:
      lang === 'cs'
        ? 'Originální airbrush design hokejové brankářské masky vytvořený na zakázku.'
        : 'Original custom airbrush design for hockey goalie masks.',
    brand: { '@type': 'Brand', name: 'Tom Art Mask' },
    offers: {
      '@type': 'AggregateOffer',
      priceCurrency: 'CZK',
      lowPrice: '8000',
      highPrice: '25000',
      offerCount: '3',
      availability: 'https://schema.org/InStock',
      seller: { '@type': 'Organization', name: 'Tom Art Mask' },
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '5.0',
      reviewCount: String(reviewCount),
      bestRating: '5',
      worstRating: '5',
    },
    review: [
      {
        '@type': 'Review',
        author: { '@type': 'Person', name: reviewToUse.name },
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '5',
          bestRating: '5',
          worstRating: '1',
        },
        reviewBody,
      },
    ],
  };

  const faqPage = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: t.faq.items.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(localBusiness)}</script>
      <script type="application/ld+json">{JSON.stringify(product)}</script>
      <script type="application/ld+json">{JSON.stringify(faqPage)}</script>
    </Helmet>
  );
};

export default StructuredData;
